import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    lunoaxReplaceGallery(this, newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
let lunoaxWipeCleanup;

function lunoaxReplaceGallery(current, next) {
  lunoaxWipeCleanup?.();

  const findImage = (gallery) =>
    [...gallery.querySelectorAll('img')]
      .filter((img) => !img.closest('dialog, slideshow-controls'))
      .map((img) => ({ img, rect: img.getBoundingClientRect() }))
      .filter(({ rect }) =>
        rect.width > 100 &&
        rect.height > 100 &&
        rect.bottom > 0 &&
        rect.top < window.innerHeight
      )
      .sort((a, b) =>
        b.rect.width * b.rect.height - a.rect.width * a.rect.height
      )[0];

  const old = findImage(current);
  const reduceMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  let cover;

  if (old?.img.complete && old.img.naturalWidth && !reduceMotion) {
    const style = getComputedStyle(old.img);

    cover = document.createElement('img');
    cover.src = old.img.currentSrc || old.img.src;
    cover.alt = '';
    cover.setAttribute('aria-hidden', 'true');

    Object.assign(cover.style, {
      position: 'fixed',
      left: `${old.rect.left}px`,
      top: `${old.rect.top}px`,
      width: `${old.rect.width}px`,
      height: `${old.rect.height}px`,
      maxWidth: 'none',
      margin: '0',
      padding: '0',
      border: '0',
      objectFit: style.objectFit,
      objectPosition: style.objectPosition,
      borderRadius: style.borderRadius,
      background: style.backgroundColor,
      pointerEvents: 'none',
      zIndex: '20',
      clipPath: 'inset(0 0 0 0)',
    });

    document.body.append(cover);
  }

  current.replaceWith(next);
  if (!cover) return;

  const controller = new AbortController();
  let animation;

  const cleanup = () => {
    clearTimeout(timer);
    controller.abort();
    animation?.cancel();
    cover.remove();

    if (lunoaxWipeCleanup === cleanup) {
      lunoaxWipeCleanup = undefined;
    }
  };

  const timer = setTimeout(cleanup, 2500);
  lunoaxWipeCleanup = cleanup;

  window.addEventListener('scroll', cleanup, {
    capture: true,
    signal: controller.signal,
  });

  window.addEventListener('resize', cleanup, {
    signal: controller.signal,
  });

  document.addEventListener('change', cleanup, {
    capture: true,
    signal: controller.signal,
  });

  requestAnimationFrame(async () => {
    try {
      const target = findImage(next);
      if (!target) return cleanup();

      const image = target.img;
      const path = (src) => new URL(src, document.baseURI).pathname;

      if (path(cover.src) === path(image.currentSrc || image.src)) {
        return cleanup();
      }

      image.loading = 'eager';
      await image.decode();

      if (controller.signal.aborted || !next.isConnected) {
        return cleanup();
      }

      const rect = image.getBoundingClientRect();

      if (
        Math.abs(rect.width - old.rect.width) > 2 ||
        Math.abs(rect.height - old.rect.height) > 2 ||
        Math.abs(rect.left - old.rect.left) > 2 ||
        Math.abs(rect.top - old.rect.top) > 2 ||
        !cover.animate
      ) {
        return cleanup();
      }

      animation = cover.animate(
        [
          { clipPath: 'inset(0 0 0 0)' },
          { clipPath: 'inset(100% 0 0 0)' },
        ],
        {
          duration: 650,
          easing: 'cubic-bezier(.22,1,.36,1)',
          fill: 'forwards',
        }
      );

      await animation.finished;
      cleanup();
    } catch {
      cleanup();
    }
  });
}